import type {
  ForumSearchParams,
  IForumSearchResponse,
  IListCommentsParams,
  IListCommentsResponse,
  IListUserCommentsParams,
  IListUserCommentsResponse,
} from '$utils/interfaces/posts';
import type { ContentType } from '$shared/lib';
import { BaseService } from './utils/BaseService';
import type {
  CreatePostBody,
  CreatePostResponse,
  EditPostBody,
  ListPostsQuery,
  ListPostsResponse,
  RetrieveForumsResponse,
  RetrievePostQuery,
  RetrievePostResponse,
} from '$shared/api';

export class ForumService extends BaseService {
  // FORUMS
  retrieveForums(): Promise<RetrieveForumsResponse> {
    return this.client.send('/api/forum/forums', {
      method: 'GET',
    });
  }

  // POSTS
  createPost(body: CreatePostBody): Promise<CreatePostResponse> {
    return this.client.send('/api/forum/post', {
      method: 'POST',
      body,
    });
  }

  updatePost(body: EditPostBody) {
    return this.client.send('/api/forum/post', {
      method: 'PUT',
      body,
    });
  }

  retrievePost(query: RetrievePostQuery): Promise<RetrievePostResponse> {
    return this.client.send('/api/forum/post', {
      method: 'GET',
      query,
    });
  }

  listPosts(query: ListPostsQuery): Promise<ListPostsResponse> {
    return this.client.send('/api/forum/posts', {
      method: 'GET',
      query,
    });
  }

  deletePost(postId: string) {
    return this.client.send('/api/forum/post', {
      method: 'DELETE',
      query: { postId },
    });
  }
  // COMMENTS
  createComment(postId: number, body: string, parentCommentId?: number) {
    return this.client.send('/api/forum/comment', {
      method: 'POST',
      body: { postId, body, parentCommentId },
    });
  }
  updateComment(id: number, body: string) {
    return this.client.send('/api/forum/comment', {
      method: 'PUT',
      body: { id, body },
    });
  }
  retrieveComment(commentId: number, fetchParent?: boolean) {
    return this.client.send('/api/forum/comment', {
      method: 'GET',
      query: { commentId, fetchParent: String(fetchParent).toLowerCase() },
    });
  }
  listComments(query?: IListCommentsParams): Promise<IListCommentsResponse> {
    return this.client.send('/api/forum/comments', {
      method: 'GET',
      query,
    });
  }
  listUserComments(
    username: string,
    query?: IListUserCommentsParams,
  ): Promise<IListUserCommentsResponse> {
    return this.client.send(`/api/forum/comments/${username}`, {
      method: 'GET',
      query,
    });
  }
  deleteComment(commentId: string) {
    return this.client.send('/api/forum/comment', {
      method: 'DELETE',
      query: { commentId },
    });
  }
  // LIKES
  createLike(likedContentType: ContentType, contentId: number) {
    return this.client.send('/api/forum/like', {
      method: 'POST',
      body: { likedContentType, contentId },
    });
  }
  listLikes(query: {
    contentId: number;
    likedContentType: ContentType;
    limit?: number;
    offset?: number;
  }) {
    return this.client.send('/api/forum/likes', {
      method: 'GET',
      query,
    });
  }
  deleteLike(likableContentType: ContentType, contentId: number) {
    return this.client.send('/api/forum/like', {
      method: 'DELETE',
      body: { likableContentType, contentId },
    });
  }
  // SEARCH
  searchForum(query?: ForumSearchParams): Promise<IForumSearchResponse> {
    return this.client.send('/api/forum/search', {
      method: 'GET',
      query,
    });
  }
}
